// Copyright (c) 2026 Goffy. This file belongs to Goffy. All Rights Reserved.

import { useEffect, useMemo, useState } from 'react';
import { StyleSheet } from 'react-native';
import { useRouter } from 'expo-router';

import GoffyButton from '@/components/GoffyButton';
import { Text, View } from '@/components/Themed';
import { useI18n } from '@/i18n';
import { getApiBaseUrl } from '@/services/apiBaseUrl';
import { appendClaimQuery } from '@/services/claimHttp';
import {
  getActiveClaimSessionSync,
  loadClaimSession,
  subscribeClaimSession,
  type ActiveClaimSession,
} from '@/services/claimSession';
import { setCurrentTourId } from '@/services/currentTour';
import { useRoundState } from '@/stores/roundState';
import appColors from '@/theme/colors';

/**
 * Start-screen card that lets a guest who claimed a player (QR code or invite link)
 * pick up where they left off without signing in.
 *
 * The claim survives on its own: the claim lock never expires server-side and the
 * claim session is persisted client-side, so the round/leaderboard routes stay open
 * to them (useAuthFlow treats them as guest-allowed). Before this card the start
 * screen ignored that session, so the only way back was re-opening the original
 * QR/invite link — the access was there, the door was not.
 */
export function ResumeClaimCard() {
  const { t } = useI18n();
  const router = useRouter();
  const apiBaseUrl = useMemo(() => getApiBaseUrl(), []);
  const [claimSession, setClaimSession] = useState<ActiveClaimSession | null>(() =>
    getActiveClaimSessionSync()
  );
  const [tourName, setTourName] = useState<string | null>(null);

  const setRoundAndHole = useRoundState((state) => state.setRoundAndHole);
  const setScorerOnlyMode = useRoundState((state) => state.setScorerOnlyMode);
  const setScorerPlayerId = useRoundState((state) => state.setScorerPlayerId);

  useEffect(() => {
    let cancelled = false;
    loadClaimSession()
      .catch(() => null)
      .finally(() => {
        if (!cancelled) {
          setClaimSession(getActiveClaimSessionSync());
        }
      });

    const unsubscribe = subscribeClaimSession((nextClaimSession) => {
      if (!cancelled) {
        setClaimSession(nextClaimSession);
      }
    });

    return () => {
      cancelled = true;
      unsubscribe();
    };
  }, []);

  // Best-effort tour name for the card copy. The claim session stores only ids, and
  // /api/tours/{tourId} is readable with claim credentials (TourAccessAuthorizationService
  // accepts a claim token, invite token, or an active claimClientId). A failure just
  // leaves the generic wording in place.
  const claimTourId = claimSession?.tourId ?? null;
  useEffect(() => {
    if (!claimTourId) {
      setTourName(null);
      return;
    }

    let cancelled = false;
    fetch(appendClaimQuery(`${apiBaseUrl}/api/tours/${claimTourId}`, claimSession))
      .then((response) => (response.ok ? response.json() : null))
      .then((data: Record<string, unknown> | null) => {
        if (cancelled || !data) return;
        const resolved =
          (data.name as string | undefined) ??
          (data.Name as string | undefined) ??
          (data.tourName as string | undefined) ??
          null;
        setTourName(resolved?.trim() || null);
      })
      .catch(() => null);

    return () => {
      cancelled = true;
    };
    // claimSession is only read for its query credentials; the tour id is what changes.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [apiBaseUrl, claimTourId]);

  if (!claimSession) {
    return null;
  }

  const playerName = claimSession.sourcePlayerName?.trim() || t('start.resumeClaimFallbackName');
  const startedRoundId = claimSession.startedRoundId?.trim() || null;

  const handleResumeScoring = () => {
    if (!startedRoundId) {
      return;
    }

    setRoundAndHole(startedRoundId, 1);
    setScorerOnlyMode(true);
    setScorerPlayerId(claimSession.sourcePlayerId ?? null);
    if (claimSession.tourId) {
      setCurrentTourId(claimSession.tourId);
    }

    const params = new URLSearchParams();
    params.set('scorerOnly', 'true');
    if (claimSession.sourcePlayerId) {
      params.set('playerId', claimSession.sourcePlayerId);
    }
    router.push(`/round/${startedRoundId}/hole/1/compact?${params.toString()}`);
  };

  const handleOpenLeaderboard = () => {
    if (!claimSession.tourId) {
      return;
    }

    setCurrentTourId(claimSession.tourId);
    router.push(`/tour/${claimSession.tourId}/leaderboard`);
  };

  return (
    <View style={styles.card} testID="resume-claim-card">
      <Text style={styles.title}>{t('start.resumeClaimTitle', { name: playerName })}</Text>
      <Text style={styles.body}>
        {t('start.resumeClaimBody', { tour: tourName ?? t('start.resumeClaimFallbackTour') })}
      </Text>
      {startedRoundId ? (
        <GoffyButton
          label={t('start.resumeClaimCta')}
          onPress={handleResumeScoring}
          tone="accent"
          style={styles.button}
          testID="resume-claim-scoring"
        />
      ) : null}
      <GoffyButton
        label={t('start.resumeClaimLeaderboardCta')}
        onPress={handleOpenLeaderboard}
        tone="dark"
        style={styles.button}
        testID="resume-claim-leaderboard"
      />
      <GoffyButton
        label={t('claimSignup.cta')}
        onPress={() => router.push('/register')}
        tone="dark"
        style={styles.button}
        testID="resume-claim-signup"
      />
    </View>
  );
}

const styles = StyleSheet.create({
  card: {
    width: '100%',
    maxWidth: 460,
    alignSelf: 'center',
    borderRadius: 16,
    borderWidth: 1,
    borderColor: appColors.border.strong,
    backgroundColor: '#07200D',
    padding: 16,
    gap: 10,
  },
  title: {
    fontSize: 18,
    fontWeight: '800',
    color: appColors.text.primary,
  },
  body: {
    fontSize: 13,
    lineHeight: 18,
    color: '#A1B49E',
  },
  button: {
    width: '100%',
  },
});
